import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { Text, Link, tokens } from '@fluentui/react-components';

/**
 * Renders tenant-provided Markdown copy (org config `additionalSupport.body`)
 * using react-markdown, mapped onto Fluent UI components so it matches the rest
 * of the Home tab.
 *
 * Safety: react-markdown does not render raw HTML (no rehype-raw plugin), and
 * links are only rendered for allow-listed protocols; anything else degrades to
 * plain text. Only a small block/inline subset of elements is allowed.
 */

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

export const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url.trim());
    return ALLOWED_PROTOCOLS.has(parsed.protocol);
  } catch {
    // Relative or malformed URLs are rejected — config must use absolute URLs.
    return false;
  }
};

const ALLOWED_ELEMENTS = ['p', 'a', 'strong', 'em', 'br', 'ul', 'ol', 'li'];

const components: Components = {
  p: ({ children }) => (
    <Text as="p" block style={{ marginTop: 0, marginBottom: tokens.spacingVerticalS }}>
      {children}
    </Text>
  ),
  a: ({ href, children }) =>
    href && isSafeUrl(href) ? (
      <Link href={href} target="_blank" rel="noreferrer">
        {children}
      </Link>
    ) : (
      // Unsafe/relative URL: degrade gracefully to the visible link text.
      <>{children}</>
    ),
};

interface MarkdownBodyProps {
  children: string;
}

export const MarkdownBody: React.FC<MarkdownBodyProps> = ({ children }) => (
  <ReactMarkdown
    components={components}
    allowedElements={ALLOWED_ELEMENTS}
    unwrapDisallowed
  >
    {children}
  </ReactMarkdown>
);
